/**
 * /en/admin/structure/paragraphs_type/patient_insurance_options/fields
 */

import { ATTRIBUTES } from "@js/constants/html-constants.es6.js";
import { VIEW_MODES } from "@js/constants/drupal-constants.es6.js";
import { SEARCH_PARAM_KEYS } from "@js/constants/js-constants.es6.js";
import Element from "@js/core/element.es6.js";
import Paragraph from "@js/core/paragraph.es6.js";
import MyChartUser from "@js/mychart-user.es6.js";
import LuminisServiceUtils from "@js/utils/luminis-service-utils.es6.js";

export default class PatientInsuranceOptionsParagraph extends Paragraph {
  #luminisServiceUtils;

  get insuranceItem() {
    return new Element({
      selector: '.insurance__item',
      parentElement: this,
    });
  }

  constructor({ parentElement }) {
    super({
      machineName: 'patient_insurance_options',
      parentElement,
    });

    this.#luminisServiceUtils = new LuminisServiceUtils();
  }

  async initialize() {
    await this.updatePatientCoverages();
  }

  async updatePatientCoverages() {
    this.#resetCoverages();

    const myChartUser = await MyChartUser.getLoggedInUser();
    if (!myChartUser) {
      // The patient is not logged in, so we cannot fetch their active insurance coverages. Do nothing.
      return;
    }

    const coverages = await this.#getCoverages();
    if (!coverages || !coverages.length) {
      // The patient does not have any applicable active coverages for this appointment. Do nothing.
      return;
    }

    coverages.forEach((coverage, index) => {
      if (coverage.payor.name) {
        this.#addCoverage(index, coverage.payor.name, coverage.plan.name);
      }
    });
  }

  async #getCoverages() {
    if (this.viewMode === VIEW_MODES.VIRTUAL_URGENT_CARE_OPEN) {
      return await this.#luminisServiceUtils.getVirtualUrgentCareCoverages();
    }

    const url = new URL(window.location.href);
    const epicDepartmentId = url.searchParams.get(SEARCH_PARAM_KEYS.EPIC_DEPARTMENT_ID);

    if (!epicDepartmentId) {
      // An Epic department ID is required to fetch insurance coverages if not in virtual urgent care mode. Do nothing.
      return;
    }

    return await this.#luminisServiceUtils.getPatientCoverages(epicDepartmentId);
  }

  /**
   * Remove payor and plan information from each currently displayed coverage
   * and reset back to the template coverage.
   */
  #resetCoverages() {
    const $insuranceItem = this.insuranceItem.$();
    $insuranceItem.empty();
    $insuranceItem.slice(1).remove();
  }

  #addCoverage(index, payorName, planName) {
    let $insuranceItem = this.insuranceItem.$(index);
    const elementExists = $insuranceItem.length > 0;

    if (!elementExists) {
      const $previousInsuranceItem = this.insuranceItem.$(index - 1);
      $insuranceItem = $previousInsuranceItem.clone();
      $previousInsuranceItem.after($insuranceItem);
    }

    let displayName = payorName;

    if (planName) {
      displayName += ` / ${planName}`;
    }

    $insuranceItem.text(displayName);
    $insuranceItem.removeAttr(ATTRIBUTES.HIDDEN);
  }
}
